import { getActivePathways } from './pathwayAssignmentService';
import { fetchProSummary } from './surveyCampaignService';

/** Active pathways marked "Report as survey", sorted by name. */
export async function getSurveyPathways() {
  const all = await getActivePathways();
  return all
    .filter((p) => p.reportAsSurvey === true)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

/** The report month for a Reports period: its last month ('YYYY-MM'). */
export function monthFromPeriod(period) {
  const keys = period?.ymKeys || [];
  return keys.length ? keys[keys.length - 1] : null;
}

/**
 * Which client a group of patients belongs to:
 * `{ kind: 'client', clientId }`, `{ kind: 'all' }` (several clients) or `{ kind: 'unassigned' }`.
 */
export function clientScopeForPatients(patients, clientSites) {
  const siteToClient = new Map((clientSites || []).map((s) => [s.id, String(s.clientId || '')]));
  const ids = new Set((patients || []).map((p) => siteToClient.get(p.clientSiteId) || ''));
  if (ids.size !== 1) return { kind: 'all' };
  const [only] = [...ids];
  return only ? { kind: 'client', clientId: only } : { kind: 'unassigned' };
}

/** Package / snapshot builder for the PRO Summary. Returns null when there is nothing to show. */
export async function buildProSummaryForContext(ctx) {
  if (!ctx.patients?.length) return null;
  const month = monthFromPeriod(ctx.period);
  if (!month) return null;
  const scope = clientScopeForPatients(ctx.patients, ctx.clientSites);
  if (scope.kind === 'unassigned') return null;
  const pathways = await getSurveyPathways();
  if (!pathways.length) return null;
  try {
    return await fetchProSummary({
      pathwayId: pathways[0].id,
      month,
      clientId: scope.kind === 'client' ? scope.clientId : null,
    });
  } catch (e) {
    console.warn('PRO summary build failed', e?.message || e);
    return null;
  }
}

/** Short labels for the five agreement levels. */
export const SCORE_LABELS = {
  5: 'Strongly agree',
  4: 'Agree',
  3: 'Neutral',
  2: 'Disagree',
  1: 'Strongly disagree',
};

export const SCORE_COLORS = {
  5: '#15803d',
  4: '#65a30d',
  3: '#a3a3a3',
  2: '#f97316',
  1: '#dc2626',
};

export function formatPct(v) {
  return v == null ? '—' : `${Math.round(v)}%`;
}

export function formatMean(v) {
  return v == null ? '—' : Number(v).toFixed(2);
}

export function formatYm(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  if (!m) return ym || '';
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 15))
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}
